import type { Breadcrumb, ErrorEvent, init } from '@sentry/react';

// `TransactionEvent` and `SpanJSON` live in @sentry/core, which isn't a direct
// dependency. Derive them from the option we're implementing so they track the
// installed Sentry version without adding a package.
type SentryOptions = NonNullable<Parameters<typeof init>[0]>;
export type SentryTransactionEvent = Parameters<NonNullable<SentryOptions['beforeSendTransaction']>>[0];
export type SentrySpan = NonNullable<SentryTransactionEvent['spans']>[number];

// OAuth credentials that some third-party endpoints only accept as query
// parameters. Sentry's default breadcrumbs integration records the full URL of
// every fetch, and httpClientIntegration attaches the URL of failed requests,
// so these have to be stripped before an event leaves the browser.
const SENSITIVE_URL_PARAMS = ['access_token', 'id_token', 'refresh_token'];

const REDACTED = '[Filtered]';

export function redactSensitiveUrl(url: string): string {
  if (!SENSITIVE_URL_PARAMS.some((param) => url.includes(param))) {
    return url;
  }

  try {
    const parsed = new URL(url, window.location.origin);
    const redactedParams = SENSITIVE_URL_PARAMS.filter((param) => parsed.searchParams.has(param));
    if (redactedParams.length === 0) {
      return url;
    }

    for (const param of redactedParams) {
      parsed.searchParams.set(param, REDACTED);
    }

    return parsed.toString();
  } catch {
    // A URL we can't parse is one we can't safely rewrite, so drop the query
    // string wholesale rather than risk shipping the credential.
    const queryStart = url.indexOf('?');
    return queryStart === -1 ? url : `${url.slice(0, queryStart)}?${REDACTED}`;
  }
}

// For values that may *contain* a URL rather than being one — span descriptions
// like "GET https://…", and free-form span attributes. Rewriting in place keeps
// the surrounding text intact, which `redactSensitiveUrl` cannot promise because
// it reparses and reserialises the whole value.
const SENSITIVE_PARAM_PATTERN = new RegExp(`(${SENSITIVE_URL_PARAMS.join('|')})=[^&\\s"']*`, 'gi');

export function redactSensitiveText(value: string): string {
  if (!SENSITIVE_URL_PARAMS.some((param) => value.includes(param))) {
    return value;
  }

  return value.replace(SENSITIVE_PARAM_PATTERN, `$1=${REDACTED}`);
}

function redactAttributes<T extends Record<string, unknown>>(attributes: T): T {
  const entries = Object.entries(attributes);
  if (!entries.some(([, value]) => typeof value === 'string' && redactSensitiveText(value) !== value)) {
    return attributes;
  }

  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  return Object.fromEntries(
    entries.map(([key, value]) => [key, typeof value === 'string' ? redactSensitiveText(value) : value]),
  ) as T;
}

function redactSpan(span: SentrySpan): SentrySpan {
  return {
    ...span,
    description: span.description == null ? span.description : redactSensitiveText(span.description),
    data: redactAttributes(span.data),
  };
}

export function redactBreadcrumbUrl(breadcrumb: Breadcrumb): Breadcrumb {
  if (typeof breadcrumb.data?.url !== 'string') {
    return breadcrumb;
  }

  return { ...breadcrumb, data: { ...breadcrumb.data, url: redactSensitiveUrl(breadcrumb.data.url) } };
}

export function redactEventUrls(event: ErrorEvent): ErrorEvent {
  if (typeof event.request?.url === 'string') {
    event.request.url = redactSensitiveUrl(event.request.url);
  }

  if (event.breadcrumbs != null) {
    event.breadcrumbs = event.breadcrumbs.map(redactBreadcrumbUrl);
  }

  return event;
}

// Transactions never reach `beforeSend`, so span payloads bypass the error-event
// scrubbing above. Sentry's fetch instrumentation records the full request URL on
// the span, which is only sampled when browser tracing is switched on.
export function redactTransactionUrls(event: SentryTransactionEvent): SentryTransactionEvent {
  if (typeof event.request?.url === 'string') {
    event.request.url = redactSensitiveUrl(event.request.url);
  }

  if (event.spans != null) {
    event.spans = event.spans.map(redactSpan);
  }

  const traceData = event.contexts?.trace?.data;
  if (traceData != null) {
    event.contexts!.trace!.data = redactAttributes(traceData);
  }

  return event;
}
